import {gsap} from "gsap/all";
import $ from "jquery";
import {BaseGame} from '/js/app/base/BaseGame.js'
import mqtt from "mqtt";
import FontFaceObserver from '../../libs/fontfaceobserver.js';
import {AudioPlaylist} from "./AudioPlaylist";
import {AudioPlayer} from "./Audio";

// Constructor
export class BaseGameBBAI extends BaseGame {

  constructor(dataFile = 'data/data.json', appType) {

    // Super constructor
    super(dataFile);

    this.appType = appType;
    this.currentCMSDataHash = null;
    this.isFetchingNewData = false;

    this.fonts = [
      // Atlas Typewriter
      new FontFaceObserver("Atlas Typewriter", {weight: 100, style: "normal"}),
      new FontFaceObserver("Atlas Typewriter", {weight: 100, style: "italic"}),
      new FontFaceObserver("Atlas Typewriter", {weight: 300, style: "normal"}),
      new FontFaceObserver("Atlas Typewriter", {weight: 300, style: "italic"}),
      new FontFaceObserver("Atlas Typewriter", {weight: 400, style: "normal"}),
      new FontFaceObserver("Atlas Typewriter", {weight: 400, style: "italic"}),
      new FontFaceObserver("Atlas Typewriter", {weight: 500, style: "normal"}),
      new FontFaceObserver("Atlas Typewriter", {weight: 500, style: "italic"}),
      new FontFaceObserver("Atlas Typewriter", {weight: 700, style: "normal"}),
      new FontFaceObserver("Atlas Typewriter", {weight: 700, style: "italic"}),
      new FontFaceObserver("Atlas Typewriter", {weight: 900, style: "normal"}),
      new FontFaceObserver("Atlas Typewriter", {weight: 900, style: "italic"}),

      // Chalet London Sixty
      new FontFaceObserver("Chalet London Sixty", {weight: 400, style: "normal"}),
    ];

    // Uptime
    this.upSince = Date.now();

    // Object class
    this.playlist = new AudioPlaylist();

    // Dedicated player for algorithm narrations — isolated from sound FX so
    // calls to app.audio.clearAll() don't kill it.
    this.narration = new AudioPlayer();

    // Animation timeline
    this.currentTimeline = null;

    // MQTT client for screenbridge communication
    this.mqttClient = null;
    this.mqttTopic = null;

    // Reset loop in case app can't connect to services or load assets
    this.restartAppOnErrorDelay = 1; // seconds

    // Wait for all
    // Load settings before everything else so settings are available to the other functions

    Promise.all([
      this.loadSettings(),
      this.loadData(),
      this.loadData("data/global.json"),
      this.loadData(`data/${appType}-global.json`),
    ]).then(() => {
      return this.loadRemoteContent();
    }).then(() => {
      // Set some base settings before rendering content if needed

      console.log(this.data);
      return this.initAppSettings();
    }).then(() => {
      // Preload audio files
      return this.preloadAudio();
    }).then(() => {
      // Preload fonts
      return this.preloadFonts();
    }).then(() => {
      // Ini nunjucks
      return this.initNunjucks();
    }).then(() => {
      // Replace strings {{ }}
      return this.renderNunjucks();
    }).then(() => {
      // Convert IMG to inline SVG
      return window.convertSVGToInline();
    }).then(() => {
      // Initiate inter-screen communication
      return this.initScreenBridgeCommunication();
    }).then(() => {
      // Real game init
      return this.initGame();
    }).catch((error) => {
      console.error("error", error)
      $("#main").show().append(`<div id='error'><p>Error: ${error.message}</p><br/><p>Restarting app in ${this.restartAppOnErrorDelay} seconds</p></div>`)

      setTimeout(() => {
        return this.restartApp();
      }, this.restartAppOnErrorDelay * 1000)
    });
  }

  // Load content from CMS JSON API (with promise)
  loadRemoteContent(closeOnFail = false) {
    console.log(`Loading remote content for ${this.appType}...`);
    return new Promise((resolve, reject) => {
      let apiUrl = this.settings.api[this.appType];
      const urlParams = new URLSearchParams(window.location.search);
      const lang = urlParams.get('lang');
      if (lang) {
        const separator = apiUrl.includes('?') ? '&' : '?';
        apiUrl += `${separator}forced_language=${lang}`;
      }
      $.ajax({
        dataType: "json",
        url: apiUrl,
        cache: false,
      }).done((data) => {
        // Here we can check if we have a hash set already (aka data has been loaded once)
        // If the hash did not change, do not update the content
        // We'll save the new hash so we can compare later
        if (this.currentCMSDataHash !== data.hash) {
          this.currentCMSDataHash = data.hash;
          this.data.cms_content = {...data.data};
          resolve(true); // true means new data available so we can take action
        } else {
          resolve(false);
        }

      }).fail(() => {
        const message = `Couldn't get new data from CMS. Please check the Internet connection and make sure the path is correct and restart the app.`;

        // If closeOnFail is set to true, the app will be closed and relaunched completely by the launcher
        if (window.inElectron() && closeOnFail === true) {
          window.electronBridge.quit()
        }

        // Otherwise, throw an error and let the caller handle it
        reject(new Error(message));
      });
    });
  };

  initAppSettings() {
    this.settings.default_language = this.data.cms_content.visitor_customization.language;
    this.i18n.set(this.settings.default_language);
  }

  initGame(defaultScreenId = 1) {
    this.initCMSDataSync();
    this.initScreenSaver();
    this.goToScreen(defaultScreenId);

    // // Simulate message on SPACEBAR
    // $(document).on('keydown', (e) => {
    //   if (e.keyCode === 32 || e.key === " ") {
    //     e.preventDefault()
    //     this.simulateMessage("PRESS");
    //   }
    // });
  }

  initCMSDataSync() {
    setInterval(async () => {
      if (this.isFetchingNewData) {
        console.log("Fetching new data skipped, already running");
        return; // skip if already fetching data
      }

      this.isFetchingNewData = true;

      try {
        const newDataAvailable = await this.loadRemoteContent();

        if (newDataAvailable) {
          if (window.inElectron()) {
            this.restartApp();
          } else {
            console.log("New data from the CMS available, please reload the webpage if you want up to date content.");
          }
        }
      } catch (err) {
        console.error("Error:", err);
      } finally {
        this.isFetchingNewData = false; // mark as done
      }

    }, this.settings.cms_data_refresh_interval_ms)
  }

  initScreenBridgeCommunication() {
    console.log("Init ScreenBridge Communication...");

    return new Promise(resolve => {
      if (!this.settings.screenbridge.topics[this.appType]) {
        console.log("No topic to subscribe, skip ScreenBridge Communication.");
        resolve();
        return;
      }

      this.mqttTopic = this.settings.screenbridge.topics[this.appType];
      const protocol = this.settings.screenbridge.protocol;
      const host = this.settings.screenbridge.host;
      const port = this.settings.screenbridge.port;
      const client = mqtt.connect(`${protocol}://${host}:${port}`, {
        clientId: 'mqttjs_' + Math.random().toString(16).substr(2, 8),
      });

      client.on("close", () => {
        if (!this.mqttClient) console.warn("MQTT not yet connected (retrying...)");
      });

      client.on("connect", () => {
        console.log("MQTT connected. Subscribing to :", this.mqttTopic);
        client.subscribe(this.mqttTopic);
        this.mqttClient = client;
      });

      client.on("message", (messageTopic, message) => {
        const messageString = message.toString();
        if (messageString === "/visitor/update") return this.restartApp()
        else if (messageString === "/reset") return this.restartApp()
        else if (messageString === "/back_to_idle") return (this.lastClick -= ((this.screensaverDelay) * 1000))
        this.onScreenBridgeMessage(messageString);
      });

      resolve(); // don't wait for MQTT so we can use app without bridge
    });
  }

  // Can be overridden in child classes
  onScreenBridgeMessage(messageString) {

  }

  // Can be overridden in child classes
  sendScreenBridgeMessage(messageString) {
    if (!this.mqttClient) return;
    // console.log("sendScreenBridgeMessage", this.mqttTopic, messageString);
    this.mqttClient.publish(this.mqttTopic, messageString);
  }

  // To be overridden in child games
  onMessage = (e) => {
    const message = e.message;
  }

  simulateMessage = (receivedString) => {
    const event = $.Event('message');
    event.message = receivedString;
    this.onMessage(event);
  }

  // Kill/reset timeline before building a new one
  killTimeline(timeline) {
    BaseGameBBAI.killTimeline(timeline);
  }

  static killTimeline(timeline) {
    if (timeline) {
      timeline.kill();
      timeline = null;
    }
  }

  // Reset values when we return to the homepage
  reset(screenId, resetCallback) {

    if (typeof screenId == "undefined") {
      screenId = 1;
    }

    // Return to idle screen
    this.i18n.set(this.settings.default_language)
    this.goToScreen(screenId, () => {
      resetCallback();
    }, true);

  }

  applyCharactersConversion() {
    // Replace characters not present in the custom font's glyph set.
    // U+2082 (₂) does not render correctly and falls back to default system font because it's not in the font,
    // However ² is in the font, so we'll swap the sub to to a sup 2 and align it at the bottom

    // NOTES for future reference:
    //  .contents() — like .children() but returns all child nodes, including text nodes and comment nodes (not just element children).
    //  On <p>Hello <b>world</b></p>, .children() gives [<b>], but .contents() gives ["Hello ", <b>].

    //  .addBack() — by default $("body").find("*") returns only descendants of <body>, not <body> itself. .addBack() adds the original selection back into the matched set.
    //  So $("body").find("*").addBack() = <body> + every descendant element.

    //  nodeType === 3 — DOM nodes have a nodeType integer:
    //   - 1 = Element node (<div>, <span>, etc.)
    //   - 3 = Text node (raw text between tags)
    //   - 8 = Comment node
    //   - 9 = Document node

    $("body").find("*").addBack().contents().filter(function () {
      return this.nodeType === 3 && this.nodeValue.includes("₂");
    }).each(function () {
      let replacement = this.nodeValue.replace(/₂/g, '<span class="sub">²</span>');
      $(this).replaceWith(replacement);
    });
  }

  setPreferredUnit() {
    $(".kpi").each((i, el) => {
      const isRange = ($(el).find(".label .en").text().trim().toLocaleLowerCase()).includes("range");
      const unit = $(el).find(".kpi-unit .en").text().trim();
      const conversion = (isRange) ? this.getTemperatureRangeConversion() : this.getTemperatureUnitConversion();
      if (!conversion) return;
      if (unit !== conversion.from) return;

      let data = $(el).data("values");
      for (let key in data) {
        data[key].en = conversion.convert(parseFloat(data[key].en)).toFixed(1);
        data[key].fr = conversion.convert(parseFloat(data[key].fr)).toFixed(1);
      }

      $(el).attr("data-values", JSON.stringify(data));
      $(el).find(".kpi-unit .en").text(conversion.unit);
      $(el).find(".kpi-unit .fr").text(conversion.unit);
    })
  }

  getTemperatureUnitConversion() {
    const preferredUnit = this.data.cms_content?.visitor_customization?.unit;
    if (preferredUnit === "celsius") {
      return {from: "°F", convert: (value) => this.fahrenheitToCelsius(value), unit: "°C"};
    } else if (preferredUnit === "fahrenheit") {
      return {from: "°C", convert: (value) => this.celsiusToFahrenheit(value), unit: "°F"};
    }
    return null;
  }

  getTemperatureRangeConversion() {
    const preferredUnit = this.data.cms_content?.visitor_customization?.unit;
    if (preferredUnit === "celsius") {
      return {from: "°F", convert: (value) => this.fahrenheitRangeToCelsiusRange(value), unit: "°C"};
    }
    return null;
  }

  fahrenheitToCelsius(value) {
    return (value - 32) * (5 / 9)
  }

  celsiusToFahrenheit(value) {
    return (value * 9 / 5) + 32
  }

  fahrenheitRangeToCelsiusRange(value) {
    return (this.fahrenheitToCelsius(32 + value))
  }

  replayVideo(selector) {
    const video = $("video", selector).get(0);
    video.currentTime = 0;
    video.play().catch(error => {
      console.error('Play failed:', error);
    });
  }
}
